/**
 * FinancialStatementScreen — Balance Sheet & P&L
 * Tree: Schedule → Primary Group → Group → Ledger
 * Column modes: 2 (Closing), 4 (Opening+Closing), 6 (Opening+Current+Closing)
 * Drilldown: click row → expand children → click ledger → GL Ledger Report
 * READ-ONLY — no editing allowed
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ChevronRight, ChevronDown, Printer, FileSpreadsheet, Download, Search } from 'lucide-react';
import { erpToast } from '../../components/erp/ERPToast';
import '../../styles/erp-theme.css';
import { API_BASE as API, getToken} from '../../lib/api';

const tkn = () => getToken() || '';
const hdrs = () => ({ Authorization: `Bearer ${tkn()}`, 'Content-Type': 'application/json' });
const fmtC = (v) => (v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 });

const FinancialStatementScreen = ({ theme = 'light', statementType = 'BS' }) => {
  const [tree, setTree] = useState([]);
  const [loading, setLoading] = useState(false);
  const [columnMode, setColumnMode] = useState(6);
  const [displayFormat, setDisplayFormat] = useState('horizontal');
  const [branchId, setBranchId] = useState('');
  const [branches, setBranches] = useState([]);
  const [expanded, setExpanded] = useState(new Set());
  const [totalDr, setTotalDr] = useState(0);
  const [totalCr, setTotalCr] = useState(0);
  const [ledgerCount, setLedgerCount] = useState(0);
  const [filterText, setFilterText] = useState('');

  const title = statementType === 'PL' ? 'Profit & Loss Statement' : 'Balance Sheet';

  useEffect(() => { (async () => { try { const r = await fetch(`${API}/api/finance-reports/branches`, { headers: hdrs() }); const j = await r.json(); if (j.success) setBranches(j.data || []); } catch {} })(); }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/financial-statements/financial-statement?category=${statementType}&branchId=${branchId}`, { headers: hdrs() });
      const j = await r.json();
      if (j.success) { setTree(j.data.tree || []); setTotalDr(j.data.totalDr); setTotalCr(j.data.totalCr); setLedgerCount(j.data.ledgerCount); }
    } catch { erpToast.error('Failed to load'); }
    setLoading(false);
  }, [statementType, branchId]);

  useEffect(() => { loadData(); }, [loadData]);

  const toggle = (id) => setExpanded(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  const renderAmountCols = (node) => {
    if (columnMode === 2) return (<>
      <td style={{ textAlign: 'right', fontFamily: 'var(--erp-font-mono)', fontSize: 12 }}>{fmtC(node.drClosing)}</td>
      <td style={{ textAlign: 'right', fontFamily: 'var(--erp-font-mono)', fontSize: 12 }}>{fmtC(node.crClosing)}</td>
    </>);
    if (columnMode === 4) return (<>
      <td style={{ textAlign: 'right', fontFamily: 'var(--erp-font-mono)', fontSize: 12 }}>{fmtC(node.drOpen)}</td>
      <td style={{ textAlign: 'right', fontFamily: 'var(--erp-font-mono)', fontSize: 12 }}>{fmtC(node.crOpen)}</td>
      <td style={{ textAlign: 'right', fontFamily: 'var(--erp-font-mono)', fontSize: 12 }}>{fmtC(node.drClosing)}</td>
      <td style={{ textAlign: 'right', fontFamily: 'var(--erp-font-mono)', fontSize: 12 }}>{fmtC(node.crClosing)}</td>
    </>);
    return (<>
      <td style={{ textAlign: 'right', fontFamily: 'var(--erp-font-mono)', fontSize: 12 }}>{fmtC(node.drOpen)}</td>
      <td style={{ textAlign: 'right', fontFamily: 'var(--erp-font-mono)', fontSize: 12 }}>{fmtC(node.crOpen)}</td>
      <td style={{ textAlign: 'right', fontFamily: 'var(--erp-font-mono)', fontSize: 12 }}>{fmtC(node.drCurrent)}</td>
      <td style={{ textAlign: 'right', fontFamily: 'var(--erp-font-mono)', fontSize: 12 }}>{fmtC(node.crCurrent)}</td>
      <td style={{ textAlign: 'right', fontFamily: 'var(--erp-font-mono)', fontSize: 12 }}>{fmtC(node.drClosing)}</td>
      <td style={{ textAlign: 'right', fontFamily: 'var(--erp-font-mono)', fontSize: 12 }}>{fmtC(node.crClosing)}</td>
    </>);
  };

  const renderHeaders = () => {
    if (columnMode === 2) return <><th style={{ textAlign: 'right', width: 150 }}>Dr Closing</th><th style={{ textAlign: 'right', width: 150 }}>Cr Closing</th></>;
    if (columnMode === 4) return <><th style={{ textAlign: 'right', width: 130 }}>Dr Opening</th><th style={{ textAlign: 'right', width: 130 }}>Cr Opening</th><th style={{ textAlign: 'right', width: 130 }}>Dr Closing</th><th style={{ textAlign: 'right', width: 130 }}>Cr Closing</th></>;
    return <><th style={{ textAlign: 'right', width: 120 }}>Dr Opening</th><th style={{ textAlign: 'right', width: 120 }}>Cr Opening</th><th style={{ textAlign: 'right', width: 120 }}>Dr Current</th><th style={{ textAlign: 'right', width: 120 }}>Cr Current</th><th style={{ textAlign: 'right', width: 120 }}>Dr Closing</th><th style={{ textAlign: 'right', width: 120 }}>Cr Closing</th></>;
  };

  const colCount = columnMode === 2 ? 4 : columnMode === 4 ? 6 : 8;

  const renderRows = () => {
    const rows = [];
    for (const sch of tree) {
      const schId = `sch-${sch.id}`;
      const isExp = expanded.has(schId);
      rows.push(
        <tr key={schId} style={{ background: 'var(--erp-primary-light)', cursor: 'pointer' }} onClick={() => toggle(schId)} data-testid={`fs-row-${sch.id}`}>
          <td style={{ fontWeight: 700, fontSize: 13, padding: '8px 12px' }} colSpan={2}>
            {isExp ? <ChevronDown size={14} style={{ marginRight: 6 }} /> : <ChevronRight size={14} style={{ marginRight: 6 }} />}
            {sch.name} <span style={{ fontSize: 10, color: 'var(--erp-text-muted)', fontWeight: 400 }}>({sch.children?.length || 0} groups)</span>
          </td>
          {renderAmountCols(sch)}
        </tr>
      );
      if (!isExp) continue;
      for (const pg of sch.children || []) {
        const pgId = `pg-${pg.id}`;
        const pgExp = expanded.has(pgId);
        rows.push(
          <tr key={pgId} style={{ background: 'var(--erp-surface-alt)', cursor: 'pointer' }} onClick={() => toggle(pgId)}>
            <td style={{ paddingLeft: 32, fontWeight: 600, fontSize: 12 }} colSpan={2}>
              {pgExp ? <ChevronDown size={12} style={{ marginRight: 4 }} /> : <ChevronRight size={12} style={{ marginRight: 4 }} />}
              {pg.name} <span style={{ fontSize: 10, color: 'var(--erp-text-muted)' }}>({pg.category})</span>
            </td>
            {renderAmountCols(pg)}
          </tr>
        );
        if (!pgExp) continue;
        for (const grp of pg.children || []) {
          const grpId = `grp-${grp.id}`;
          const grpExp = expanded.has(grpId);
          rows.push(
            <tr key={grpId} style={{ cursor: 'pointer' }} onClick={() => toggle(grpId)}>
              <td style={{ paddingLeft: 56, fontWeight: 600, fontSize: 12 }} colSpan={2}>
                {grpExp ? <ChevronDown size={11} style={{ marginRight: 4 }} /> : <ChevronRight size={11} style={{ marginRight: 4 }} />}
                {grp.name} <span style={{ fontSize: 10, color: 'var(--erp-text-muted)' }}>({grp.ledgerCount} ledgers)</span>
              </td>
              {renderAmountCols(grp)}
            </tr>
          );
          if (!grpExp) continue;
          for (const gl of grp.children || []) {
            rows.push(
              <tr key={`gl-${gl.id}`} style={{ fontSize: 11 }}>
                <td style={{ paddingLeft: 72, fontFamily: 'var(--erp-font-mono)' }}>{gl.id}</td>
                <td>{gl.name}</td>
                {renderAmountCols(gl)}
              </tr>
            );
          }
        }
      }
    }
    return rows;
  };

  return (
    <div className="erp-module" data-testid="financial-statement">
      <div className="erp-screen-header">
        <span className="erp-screen-title">{title}</span>
        <span style={{ fontSize: 12, fontWeight: 600, marginLeft: 16, color: 'var(--erp-primary)' }}>Ledgers: {ledgerCount} | Dr: {fmtC(totalDr)} | Cr: {fmtC(totalCr)}</span>
        <div className="erp-screen-actions">
          <input value={filterText} onChange={e => setFilterText(e.target.value)} placeholder="Search..." className="erp-input" style={{ width: 140, height: 26, fontSize: 11 }} />
          <button className="erp-btn erp-btn-ghost" onClick={() => { const csv = 'Name,DrClosing,CrClosing\n' + (tree.flatMap(s => (s.children||[]).map(pg => `"${pg.name}",${pg.drClosing},${pg.crClosing}`))).join('\n'); const b = new Blob(['\ufeff'+csv],{type:'text/csv'}); const a=document.createElement('a'); a.href=URL.createObjectURL(b); a.download=`${title}.csv`; a.click(); }}><Download size={13} /> CSV</button>
          <button className="erp-btn erp-btn-ghost" onClick={() => window.print()}><Printer size={13} /> Print/PDF</button>
        </div>
      </div>

      <div style={{ padding: 'var(--erp-sp-3)', borderBottom: '1px solid var(--erp-border)', display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
        <div className="erp-form-row" style={{ margin: 0 }}><label className="erp-form-label">Statement</label><div className="erp-form-control"><input value={statementType === 'PL' ? 'Profit & Loss' : 'Balance Sheet'} readOnly className="erp-input" style={{ width: 130, background: 'var(--erp-hover)', fontWeight: 600 }} /></div></div>
        <div className="erp-form-row" style={{ margin: 0 }}><label className="erp-form-label">Branch</label><div className="erp-form-control"><select value={branchId} onChange={e => setBranchId(e.target.value)} className="erp-input erp-select" style={{ width: 140 }}><option value="">All Branches</option>{branches.map(b => <option key={b.ID} value={b.ID}>{b.Name}</option>)}</select></div></div>
        <div className="erp-form-row" style={{ margin: 0 }}><label className="erp-form-label">Columns</label><div className="erp-form-control">
          <select value={columnMode} onChange={e => setColumnMode(parseInt(e.target.value))} className="erp-input erp-select" style={{ width: 200 }}>
            <option value={2}>2 Columns (Closing Balances)</option>
            <option value={4}>4 Columns (Opening + Closing)</option>
            <option value={6}>6 Columns (Opening + Current + Closing)</option>
          </select>
        </div></div>
        <div className="erp-form-row" style={{ margin: 0 }}><label className="erp-form-label">Format</label><div className="erp-form-control">
          <select value={displayFormat} onChange={e => setDisplayFormat(e.target.value)} className="erp-input erp-select" style={{ width: 130 }}>
            <option value="horizontal">Horizontal</option>
            <option value="vertical">Vertical (T-Format)</option>
          </select>
        </div></div>
      </div>

      {displayFormat === 'vertical' ? (
        /* ── VERTICAL T-FORMAT with column modes ── */
        <div style={{ flex: 1, display: 'flex', gap: 1, overflow: 'auto' }}>
          {/* LEFT: Debit/Assets/Expenses */}
          <div style={{ flex: 1, borderRight: '2px solid var(--erp-primary)', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '6px 12px', background: 'var(--erp-primary)', color: '#fff', fontWeight: 700, fontSize: 13, textAlign: 'center' }}>
              {statementType === 'PL' ? 'Expenses' : 'Assets'} (Debit)
            </div>
            <div style={{ flex: 1, overflow: 'auto' }}>
              {tree.map(sch => (sch.children || []).filter(pg => pg.drClosing > pg.crClosing).map(pg => (
                <div key={pg.id} style={{ padding: '6px 12px', borderBottom: '1px solid var(--erp-border)' }}>
                  <div style={{ fontWeight: 600, fontSize: 12, display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                    <span style={{ flex: 1 }}>{pg.name}</span>
                    {columnMode >= 4 && <span style={{ fontFamily: 'var(--erp-font-mono)', fontSize: 10, color: 'var(--erp-text-muted)' }}>Open: {fmtC(pg.drOpen)}</span>}
                    {columnMode >= 6 && <span style={{ fontFamily: 'var(--erp-font-mono)', fontSize: 10, color: 'var(--erp-text-muted)' }}>Curr: {fmtC(pg.drCurrent)}</span>}
                    <span style={{ fontFamily: 'var(--erp-font-mono)', fontWeight: 700 }}>{fmtC(pg.drClosing - pg.crClosing)}</span>
                  </div>
                  {(pg.children || []).map(grp => (
                    <div key={grp.id} style={{ padding: '2px 0 2px 16px', fontSize: 11, display: 'flex', justifyContent: 'space-between', gap: 8, color: 'var(--erp-text-muted)' }}>
                      <span style={{ flex: 1 }}>{grp.name}</span>
                      {columnMode >= 4 && <span style={{ fontFamily: 'var(--erp-font-mono)', fontSize: 10 }}>{fmtC(grp.drOpen)}</span>}
                      {columnMode >= 6 && <span style={{ fontFamily: 'var(--erp-font-mono)', fontSize: 10 }}>{fmtC(grp.drCurrent)}</span>}
                      <span style={{ fontFamily: 'var(--erp-font-mono)' }}>{fmtC(grp.drClosing - grp.crClosing)}</span>
                    </div>
                  ))}
                </div>
              )))}
              <div style={{ padding: '8px 12px', background: 'var(--erp-primary-light)', fontWeight: 700, fontSize: 12, textAlign: 'right', fontFamily: 'var(--erp-font-mono)' }}>Total: {fmtC(totalDr)}</div>
            </div>
          </div>
          {/* RIGHT: Credit/Liabilities/Income */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '6px 12px', background: 'var(--erp-primary)', color: '#fff', fontWeight: 700, fontSize: 13, textAlign: 'center' }}>
              {statementType === 'PL' ? 'Income' : 'Liabilities'} (Credit)
            </div>
            <div style={{ flex: 1, overflow: 'auto' }}>
              {tree.map(sch => (sch.children || []).filter(pg => pg.crClosing > pg.drClosing).map(pg => (
                <div key={pg.id} style={{ padding: '6px 12px', borderBottom: '1px solid var(--erp-border)' }}>
                  <div style={{ fontWeight: 600, fontSize: 12, display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                    <span style={{ flex: 1 }}>{pg.name}</span>
                    {columnMode >= 4 && <span style={{ fontFamily: 'var(--erp-font-mono)', fontSize: 10, color: 'var(--erp-text-muted)' }}>Open: {fmtC(pg.crOpen)}</span>}
                    {columnMode >= 6 && <span style={{ fontFamily: 'var(--erp-font-mono)', fontSize: 10, color: 'var(--erp-text-muted)' }}>Curr: {fmtC(pg.crCurrent)}</span>}
                    <span style={{ fontFamily: 'var(--erp-font-mono)', fontWeight: 700 }}>{fmtC(pg.crClosing - pg.drClosing)}</span>
                  </div>
                  {(pg.children || []).map(grp => (
                    <div key={grp.id} style={{ padding: '2px 0 2px 16px', fontSize: 11, display: 'flex', justifyContent: 'space-between', gap: 8, color: 'var(--erp-text-muted)' }}>
                      <span style={{ flex: 1 }}>{grp.name}</span>
                      {columnMode >= 4 && <span style={{ fontFamily: 'var(--erp-font-mono)', fontSize: 10 }}>{fmtC(grp.crOpen)}</span>}
                      {columnMode >= 6 && <span style={{ fontFamily: 'var(--erp-font-mono)', fontSize: 10 }}>{fmtC(grp.crCurrent)}</span>}
                      <span style={{ fontFamily: 'var(--erp-font-mono)' }}>{fmtC(grp.crClosing - grp.drClosing)}</span>
                    </div>
                  ))}
                </div>
              )))}
              <div style={{ padding: '8px 12px', background: 'var(--erp-primary-light)', fontWeight: 700, fontSize: 12, textAlign: 'right', fontFamily: 'var(--erp-font-mono)' }}>Total: {fmtC(totalCr)}</div>
            </div>
          </div>
        </div>
      ) : (
      /* ── HORIZONTAL FORMAT (default tree grid) ── */
      <div className="erp-grid-wrapper" style={{ flex: 1, margin: 0, borderRadius: 0 }}>
        <div className="erp-grid-scroll">
          <table className="erp-grid" data-testid="fs-tree-grid">
            <thead><tr><th style={{ minWidth: 80 }}>Code</th><th style={{ minWidth: 200 }}>Name</th>{renderHeaders()}</tr></thead>
            <tbody>
              {loading ? <tr><td colSpan={colCount} style={{ padding: 48, textAlign: 'center', color: 'var(--erp-text-muted)' }}>Loading financial statement...</td></tr>
              : tree.length === 0 ? <tr><td colSpan={colCount} style={{ padding: 48, textAlign: 'center', color: 'var(--erp-text-muted)' }}>No data available</td></tr>
              : renderRows()}
            </tbody>
          </table>
        </div>
      </div>
      )}
    </div>
  );
};

export default FinancialStatementScreen;
